import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ClearIcon from '@mui/icons-material/Clear';
import LockIcon from '@mui/icons-material/Lock';
import BadgeIcon from '@mui/icons-material/Badge';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import patientService from '../services/patientService';
import PageHeader from '../components/PageHeader';
import type { PatientSearchResult, PatientReportEntry } from '../services/patientService';
import { typography } from '~/theme';

// ── Patient History View ───────────────────────────────────────────────

interface PatientHistoryViewProps {
  patient: PatientSearchResult;
  onBack: () => void;
}

const PatientHistoryView: React.FC<PatientHistoryViewProps> = ({ patient, onBack }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<PatientReportEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [sortField, setSortField] = useState<'study_date' | 'modality' | 'diagnosis_category'>('study_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await patientService.getPatientReports(patient.patient_id);
      setReports(resp.reports);
      setLoaded(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load patient reports');
    } finally {
      setLoading(false);
    }
  }, [patient.patient_id]);

  React.useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedReports = [...reports].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    const cmp = aVal.localeCompare(bVal);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const sortIndicator = (field: typeof sortField) =>
    sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <Box>
      <PageHeader
        title="Patient History"
        onBack={onBack}
        backLabel="Back to Search"
      />

      {/* Patient Info Card */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LockIcon fontSize="small" color="success" />
          <Typography variant="caption" color="text.secondary">
            PHI Encrypted — Decrypted for authorized view only
          </Typography>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Patient Name</Typography>
            <Typography variant="body2" fontWeight={typography.label.fontWeight}>{patient.display_name}</Typography>
          </Box>
          {patient.mrn && (
            <Box>
              <Typography variant="caption" color="text.secondary">MRN</Typography>
              <Typography variant="body2" fontWeight={typography.label.fontWeight}>{patient.mrn}</Typography>
            </Box>
          )}
          {patient.dob && (
            <Box>
              <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
              <Typography variant="body2" fontWeight={typography.label.fontWeight}>{patient.dob}</Typography>
            </Box>
          )}
          {patient.gender && (
            <Box>
              <Typography variant="caption" color="text.secondary">Gender</Typography>
              <Typography variant="body2" fontWeight={typography.label.fontWeight}>{patient.gender}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">Total Reports</Typography>
            <Typography variant="body2" fontWeight={typography.label.fontWeight}>{patient.report_count}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Study Timeline / Reports */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={typography.label.fontWeight} sx={{ mb: 1.5 }}>
          Previous Reports ({reports.length})
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {loaded && reports.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No reports found for this patient.
          </Typography>
        )}

        {reports.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('study_date')}
                  >
                    Study Date{sortIndicator('study_date')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('modality')}
                  >
                    Modality{sortIndicator('modality')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('diagnosis_category')}
                  >
                    Diagnosis{sortIndicator('diagnosis_category')}
                  </TableCell>
                  <TableCell>Pathology</TableCell>
                  <TableCell>Anatomy</TableCell>
                  <TableCell>Session</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedReports.map((r) => (
                  <TableRow key={r.report_id} hover>
                    <TableCell>{r.study_date || '—'}</TableCell>
                    <TableCell>
                      {r.modality ? (
                        <Chip label={r.modality} size="small" variant="outlined" />
                      ) : '—'}
                    </TableCell>
                    <TableCell>{r.diagnosis_category || '—'}</TableCell>
                    <TableCell>{r.pathology_type || '—'}</TableCell>
                    <TableCell>{r.anatomy_region || '—'}</TableCell>
                    <TableCell>
                      {r.session_id ? (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {r.session_id.substring(0, 8)}…
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {r.session_id ? (
                        <Tooltip title="Open in Transcription Page">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/transcribe?session=${r.session_id}`)}
                            color="primary"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

// ── Main Patient Search Page ───────────────────────────────────────────

const PatientSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBackInApp = Boolean(location.key && location.key !== 'default');
  const { patientId: patientIdParam } = useParams<{ patientId: string }>();
  const [searchParams] = useSearchParams();
  const [searchFields, setSearchFields] = useState({
    name: '',
    mrn: '',
    phone: '',
    email: '',
  });
  const [results, setResults] = useState<PatientSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchResult | null>(null);

  // Auto-load patient from URL param (/patients/:patientId) or query string (?patient_id=).
  useEffect(() => {
    const idToLoad = patientIdParam ?? searchParams.get('patient_id');
    if (!idToLoad) return;
    setLoading(true);
    patientService.getPatient(idToLoad)
      .then(p => {
        setSelectedPatient({
          patient_id: p.id,
          display_name: [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Patient',
          dob: p.dob || null,
          mrn: p.mrn || null,
          gender: p.gender || null,
          report_count: 0,
        });
      })
      .catch(() => setError('Could not load patient details.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientIdParam]);

  const handleFieldChange = (field: keyof typeof searchFields) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchFields(prev => ({ ...prev, [field]: e.target.value }));
  };

  const hasSearchCriteria = Object.values(searchFields).some(v => v.trim() !== '');

  const handleSearch = async () => {
    if (!hasSearchCriteria) return;

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const params: Record<string, string> = {};
      if (searchFields.name.trim()) params.name = searchFields.name.trim();
      if (searchFields.mrn.trim()) params.mrn = searchFields.mrn.trim();
      if (searchFields.phone.trim()) params.phone = searchFields.phone.trim();
      if (searchFields.email.trim()) params.email = searchFields.email.trim();

      const resp = await patientService.searchPatients(params);
      setResults(resp.patients);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchFields({ name: '', mrn: '', phone: '', email: '' });
    setResults([]);
    setSearched(false);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && hasSearchCriteria) {
      handleSearch();
    }
  };

  // If a patient is selected (via state or URL param), show the history view
  if (selectedPatient) {
    return (
      <Box sx={{ height: '100%', overflow: 'auto', p: 1 }}>
        <PatientHistoryView
          patient={selectedPatient}
          onBack={() => {
            setSelectedPatient(null);
            if (canGoBackInApp) navigate(-1); else navigate('/patients');
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 1 }}>
      <PageHeader
        title="Patient Search"
        onBack={() => canGoBackInApp ? navigate(-1) : navigate('/')}
        backLabel="Back"
        actions={
          <Chip
            icon={<LockIcon />}
            label="PHI Encrypted"
            size="small"
            color="success"
            variant="outlined"
          />
        }
      />

      {/* Security Notice */}
      <Alert severity="info" icon={<LockIcon />} sx={{ mb: 2 }}>
        All searches use <strong>secure hashed tokens</strong>. Patient identifiers are never used
        in database queries directly. All search events are logged in the PHI audit trail.
      </Alert>

      {/* Search Form */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Search by any combination of fields
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 1.5,
          }}
          onKeyDown={handleKeyDown}
        >
          <TextField
            label="Patient Name"
            value={searchFields.name}
            onChange={handleFieldChange('name')}
            placeholder="First Last"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <TextField
            label="MRN"
            value={searchFields.mrn}
            onChange={handleFieldChange('mrn')}
            placeholder="e.g. 12345"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><BadgeIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <TextField
            label="Phone Number"
            value={searchFields.phone}
            onChange={handleFieldChange('phone')}
            placeholder="e.g. +1 (555) 111-2222"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <TextField
            label="Email"
            value={searchFields.email}
            onChange={handleFieldChange('email')}
            placeholder="patient@example.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={!hasSearchCriteria || loading}
          >
            {loading ? 'Searching…' : 'Search'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Results */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {searched && !loading && !error && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Results ({results.length})
          </Typography>

          {results.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No patients found matching your criteria.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>MRN</TableCell>
                    <TableCell>Date of Birth</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell align="center">Reports</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((p) => (
                    <TableRow
                      key={p.patient_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/patients/${p.patient_id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={typography.label.fontWeight}>{p.display_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {p.mrn || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{p.dob || '—'}</TableCell>
                      <TableCell>{p.gender || '—'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={p.report_count}
                          size="small"
                          color={p.report_count > 0 ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View patient history">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/patients/${p.patient_id}`);
                            }}
                          >
                            <PersonSearchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default PatientSearchPage;
